import { cn } from '@/lib/utils'
import {
    NotificationMessage,
    NotificationVisualMessage,
} from '@/service/api/endpoints/notificacoes'
import { CircularProgress } from '@mui/material'
import {
    BadgeAlertIcon,
    BadgeCheckIcon,
    BadgeXIcon,
    ExternalLinkIcon,
    PinIcon,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { StoredNotification } from './Central'

type Notf = NotificationMessage & { seen?: boolean }

interface IProps extends React.HTMLAttributes<HTMLDivElement> {
    data: Notf | StoredNotification
}

export default function Notificacao({ data, className, ...props }: IProps) {
    const navigate = useNavigate()

    if ('value' in data && typeof data.value === 'number') {
        const label =
            data.type === 'pdf'
                ? `Progresso do PDF ${data.ref}`
                : `Progresso do(a) ${data.ref}`
        return (
            <div
                className={cn(
                    'relative',
                    'p-4 rounded-md bg-card ring-1 ring-primary text-foreground flex items-center gap-3',
                    className,
                )}
                {...props}
            >
                <CircularProgress
                    size={18}
                    thickness={8}
                    variant="determinate"
                    value={data.value}
                    className="text-foreground shrink-0"
                />
                <span className="text-sm">
                    {label}: {data.value}%
                </span>
                <sup className="absolute -top-2 -right-1.5 text-foreground rounded-full flex items-center justify-center p-1">
                    <PinIcon className="size-4 rotate-45" />
                </sup>
            </div>
        )
    }

    const notification =
        (data as Notf).mode === 'silent'
            ? null
            : (data as NotificationVisualMessage & { seen?: boolean })

    if (notification === null) return null

    return (
        <div
            title={notification.onClickUrl}
            className={cn(
                'p-4 rounded-md border bg-card transition-colors duration-200',
                'border-primary hover:bg-primary/10 flex flex-row gap-4',
                {
                    'cursor-pointer': !!notification.onClickUrl,
                    'ring-2 ring-primary': !notification.seen,
                    'hover:bg-success/10': notification.severity === 'success',
                    'hover:bg-warning/10': notification.severity === 'warning',
                    'hover:bg-error/10': notification.severity === 'error',
                },
                className,
            )}
            onClick={() => {
                if (!notification.onClickUrl) return

                notification.onClickUrl.startsWith('http')
                    ? window.open(notification.onClickUrl, '_blank')
                    : navigate(notification.onClickUrl)
            }}
            {...props}
        >
            <div
                className={cn('flex items-center h-full', {
                    'animate-wiggle': !notification.seen,
                })}
            >
                {notification.severity === 'success' && (
                    <BadgeCheckIcon className="text-success" />
                )}
                {notification.severity === 'warning' && (
                    <BadgeAlertIcon className="text-warning" />
                )}
                {notification.severity === 'error' && (
                    <BadgeXIcon className="text-error" />
                )}
            </div>
            <div className="flex flex-col">
                <div className="w-full flex justify-between items-center mb-1">
                    <div className="flex items-center gap-2 float-left">
                        <span
                            className={cn(
                                'text-sm inline-flex items-center gap-2',
                                {
                                    'font-bold': !notification.seen,
                                    'font-semibold': notification.seen,
                                },
                            )}
                        >
                            <span
                                className={cn(
                                    'hidden bg-primary rounded-full size-2',
                                    {
                                        block: !notification.seen,
                                    },
                                )}
                                aria-label="Notificacao não lida"
                            />
                            {notification.title || 'Notificação'}
                        </span>
                    </div>
                    {notification.onClickUrl && (
                        <ExternalLinkIcon className="size-4 float-right" />
                    )}
                </div>
                <p
                    className={cn(
                        'text-xs line-clamp-3 overflow-hidden text-ellipsis',
                        {
                            'text-foreground': !notification.seen,
                        },
                    )}
                    title={notification.message}
                >
                    {notification.message}
                </p>
            </div>
        </div>
    )
}
